import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, Paper, Button, Chip, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  CircularProgress, InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import { apiGet, apiPost, apiPatch, apiDelete, ApiError } from "../api/client";
import { useToast } from "../ui/Toaster";

const PERIODS = [
  { value: "week",     label: "Weekly" },
  { value: "month",    label: "Monthly" },
  { value: "quarter",  label: "Quarterly" },
  { value: "year",     label: "Yearly" },
  { value: "one_time", label: "One-time" },
];
const PERIOD_LABEL = Object.fromEntries(PERIODS.map((p) => [p.value, p.label]));

const STATUSES = [
  { value: "active",    label: "Active" },
  { value: "cancelled", label: "Cancelled" },
];

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

function fmtDate(epoch) {
  if (!epoch) return "—";
  const d = new Date(Number(epoch) * 1000);
  return d.toLocaleDateString();
}

function relUntil(epoch) {
  if (!epoch) return "";
  const diff = Math.floor(Number(epoch) - Date.now() / 1000);
  if (diff <= 0) return "due";
  const days = Math.floor(diff / 86400);
  if (days < 1) return "today";
  if (days === 1) return "tomorrow";
  if (days < 30) return `in ${days}d`;
  if (days < 365) return `in ${Math.floor(days / 30)}mo`;
  return `in ${Math.floor(days / 365)}y`;
}

// Convert a YYYY-MM-DD string from a date <input> to an epoch second, and back.
function dateInputToEpoch(s) {
  if (!s) return null;
  const t = new Date(`${s}T12:00:00`).getTime();
  return Number.isFinite(t) ? Math.floor(t / 1000) : null;
}
function epochToDateInput(epoch) {
  if (!epoch) return "";
  const d = new Date(Number(epoch) * 1000);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function StatusChip({ status }) {
  if (status === "cancelled") {
    return <Chip size="small" label="Cancelled" variant="outlined" />;
  }
  return <Chip size="small" label="Active" color="success" variant="outlined" />;
}

function SubscriptionDialog({ open, initial, onClose, onSaved }) {
  const { error: toastError, success: toastSuccess } = useToast();
  const editing = !!initial?.id;
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => ({
    name: "",
    retailer: "",
    category: "",
    amount: "",
    period: "month",
    status: "active",
    started_at: "",       // YYYY-MM-DD strings; converted on submit
    next_renewal_at: "",
    notes: "",
  }));

  useEffect(() => {
    if (!open) return;
    setForm({
      name:            initial?.name || "",
      retailer:        initial?.retailer || "",
      category:        initial?.category || "",
      amount:          initial?.amount != null ? String(initial.amount) : "",
      period:          initial?.period || "month",
      status:          initial?.status || "active",
      started_at:      epochToDateInput(initial?.started_at),
      next_renewal_at: epochToDateInput(initial?.next_renewal_at),
      notes:           initial?.notes || "",
    });
  }, [open, initial]);

  const setField = (k) => (e) => setForm((cur) => ({ ...cur, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) {
      toastError("Name is required");
      return;
    }
    const amount = Number(form.amount || 0);
    if (!Number.isFinite(amount) || amount < 0) {
      toastError("Amount must be a non-negative number");
      return;
    }
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      retailer: form.retailer.trim(),
      category: form.category.trim(),
      amount,
      period: form.period,
      status: form.status,
      started_at: dateInputToEpoch(form.started_at),
      next_renewal_at: dateInputToEpoch(form.next_renewal_at),
      notes: form.notes.trim(),
    };
    try {
      if (editing) {
        await apiPatch(`/api/subscriptions/${initial.id}`, payload);
        toastSuccess("Saved", form.name);
      } else {
        await apiPost("/api/subscriptions", payload);
        toastSuccess("Added", form.name);
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toastError("Couldn't save", e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? "Edit subscription" : "Add subscription"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Name" value={form.name} onChange={setField("name")}
            autoFocus required size="small" fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Amount" value={form.amount} onChange={setField("amount")}
              type="number" inputProps={{ min: 0, step: "0.01" }}
              size="small" sx={{ flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
            <TextField
              select label="Period" value={form.period} onChange={setField("period")}
              size="small" sx={{ flex: 1 }}
            >
              {PERIODS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Retailer (optional)" value={form.retailer} onChange={setField("retailer")}
              size="small" sx={{ flex: 1 }}
              placeholder="Adobe, AWS…"
            />
            <TextField
              label="Category (optional)" value={form.category} onChange={setField("category")}
              size="small" sx={{ flex: 1 }}
              placeholder="Software, Hosting…"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Started" value={form.started_at} onChange={setField("started_at")}
              type="date" size="small" sx={{ flex: 1 }} InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Next renewal (optional override)" value={form.next_renewal_at}
              onChange={setField("next_renewal_at")}
              type="date" size="small" sx={{ flex: 1 }} InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <TextField
            select label="Status" value={form.status} onChange={setField("status")}
            size="small" fullWidth
          >
            {STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Notes (optional)" value={form.notes} onChange={setField("notes")}
            size="small" fullWidth multiline minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={submit} variant="contained" disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {editing ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Subscriptions() {
  const { error: toastError, success: toastSuccess } = useToast();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active"); // active | all | cancelled
  const [editing, setEditing] = useState(null);   // existing row when editing
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("/api/subscriptions");
      setRows(Array.isArray(res?.results) ? res.results : []);
      setSummary(res?.summary || {});
    } catch (e) {
      toastError("Couldn't load subscriptions", e instanceof ApiError ? e.message : String(e));
      setRows([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  }, [toastError]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  const remove = async (row) => {
    if (!window.confirm(`Delete subscription "${row.name}"? This cannot be undone.`)) return;
    try {
      await apiDelete(`/api/subscriptions/${row.id}`);
      toastSuccess("Deleted", row.name);
      load();
    } catch (e) {
      toastError("Couldn't delete", e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: "1.85rem", md: "2.5rem" } }}>
            Subscriptions
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            Track recurring expenses and project monthly burn.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="outlined"
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setAdding(true)}
          >
            Add
          </Button>
        </Stack>
      </Stack>

      {/* Summary tiles */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.75, flex: 1, minWidth: 150 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontSize: 11 }}>
            Monthly burn
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {money(summary.monthly_burn)}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.75, flex: 1, minWidth: 150 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontSize: 11 }}>
            Yearly projection
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {money(summary.yearly_projection)}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.75, flex: 1, minWidth: 150 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontSize: 11 }}>
            Active
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {summary.active_count ?? 0}
          </Typography>
        </Paper>
        {summary.one_time_pending > 0 && (
          <Paper variant="outlined" sx={{ p: 1.75, flex: 1, minWidth: 150 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontSize: 11 }}>
              One-time pending
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
              {money(summary.one_time_pending)}
            </Typography>
          </Paper>
        )}
      </Stack>

      {/* Status filter chips */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.5 }}>
            Show:
          </Typography>
          {["active", "all", "cancelled"].map((k) => (
            <Chip
              key={k}
              size="small"
              label={k[0].toUpperCase() + k.slice(1)}
              color={statusFilter === k ? "primary" : "default"}
              variant={statusFilter === k ? "filled" : "outlined"}
              onClick={() => setStatusFilter(k)}
            />
          ))}
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Next renewal</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ width: 90 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    {rows.length === 0
                      ? "No subscriptions yet. Click Add to track your first one."
                      : "Nothing matches this filter."}
                  </TableCell>
                </TableRow>
              )}
              {visible.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.name}</Typography>
                    {row.retailer && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {row.retailer}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{row.category || "—"}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {money(row.amount)}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {PERIOD_LABEL[row.period] || row.period}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {row.period === "one_time" && !row.next_renewal_at ? "—" : (
                      <>
                        {fmtDate(row.next_renewal_at)}
                        {row.next_renewal_at && (
                          <Box component="span" sx={{ ml: 1, fontSize: 11, color: "text.disabled" }}>
                            {relUntil(row.next_renewal_at)}
                          </Box>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell><StatusChip status={row.status} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditing(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => remove(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SubscriptionDialog
        open={adding || !!editing}
        initial={editing}
        onClose={() => { setAdding(false); setEditing(null); }}
        onSaved={load}
      />
    </Box>
  );
}
